import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from '@/shared/toast';
import { useAuth } from "@/auth/session";
import { RequireAuth, RedirectIfAuthed } from "@/auth/guards";
import { ThemeProvider } from "@/providers/theme-provider";
import {
  OfflineDetector,
  OnlineStatusIndicator,
} from "@/components/offline-detector";
import ErrorBoundary from "@/components/error-boundary";

// Pages
import SimpleReactDashboard from "./pages/simple-react-dashboard";
import DirectParentDashboard from "@/pages/direct-parent-dashboard";
import IndependentDashboard from "@/pages/independent-dashboard";
import EnhancedParentDashboard from "@/pages/parent/enhanced-dashboard";
import ParentAnnouncementsPage from "@/pages/parent/announcements";
import ParentPaymentsPage from "@/pages/parent/Payments";
import ParentPaymentDetailPage from "@/pages/parent/PaymentDetail";
import AuthPageDev from "@/pages/auth-page-dev";
import Dashboard from "@/pages/dashboard";
import PlayersPage from "@/pages/players-page";
import SchedulePage from "@/pages/schedule-page";
import AnnouncementsPage from "@/pages/announcements-page";
import ProfilePage from "@/pages/profile-page";
import NotFound from "@/pages/not-found";
import ForceLogoutPage from "@/pages/force-logout";
import RegisterDebug from "@/pages/register-debug";

// Admin Pages
import AdminDashboard from "@/pages/admin/admin-dashboard";
import CoachesPendingApprovalPage from "@/pages/admin/coaches-pending-approval";
import AdminFitness from "@/pages/admin/Fitness";
import AdminReminders from "@/pages/admin/AdminReminders";
import PlayerImportPage from "@/pages/admin/PlayerImportPage";
import PlayersPendingReviewPage from "@/pages/admin/players-pending-review";
import AiTaskQueuePage from "@/pages/admin/AiTaskQueuePage";
import AgentSchedulesPage from "@/pages/admin/AgentSchedulesPage";

// Coach Pages
import CoachSchedule from "@/pages/coach/CoachSchedule";
import CoachDashboard from "@/pages/coach/CoachDashboard";
import TeamManagement from "@/pages/coach/TeamManagement";
import TeamDetail from "@/pages/coach/TeamDetail";
import BatchesPage from "@/pages/coach/BatchesPage";
import BatchAnalyticsPage from "@/pages/coach/BatchAnalyticsPage";
import CoachSessions from "@/pages/coach/CoachSessions";
import SessionDetail from "@/pages/coach/SessionDetail";
import CoachBatchEntry from "@/pages/coach/CoachBatchEntry";
import CoachKidDetail from "@/pages/coach/CoachKidDetail";
import PerformanceCapturePage from "@/pages/coach/PerformanceCapturePage";
import HubTournamentDemo from "@/pages/hub/HubTournamentDemo";
import HubSelectionDemo from "@/pages/hub/HubSelectionDemo";
import HubDashboardDemo from "@/pages/hub/HubDashboardDemo";
import HubComingSoonStub from "@/pages/hub/HubComingSoonStub";
import HubPerformanceStatsDemo from "@/pages/hub/HubPerformanceStatsDemo";
import HubReportsDemo from "@/pages/hub/HubReportsDemo";
import HubAnnouncementsDemo from "@/pages/hub/HubAnnouncementsDemo";
import HubGamesScheduleDemo from "@/pages/hub/HubGamesScheduleDemo";
import HubTeamsRostersDemo from "@/pages/hub/HubTeamsRostersDemo";
import HubAgeVerificationDemo from "@/pages/hub/HubAgeVerificationDemo";
import HubFitnessTestsDemo from "@/pages/hub/HubFitnessTestsDemo";
// ParentFitness removed — /parent/fitness route does not exist. Re-add when fitness feature is ready.
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import AddKid from "@/pages/parent/AddKid";
import ParentSchedule from "@/pages/parent/Schedule";
import ParentAvailability from "@/pages/parent/Availability";
import { flags } from "@/utils/featureFlags";

// Dashboard Pages
import { DashboardLayout } from "@/layout/DashboardLayout";
import { ParentDashboardLayout } from "@/layout/ParentDashboardLayout";
import TeamPage from "@/pages/dashboard/TeamPage";
import DashboardAnnouncementsPage from "@/pages/dashboard/AnnouncementsPage";
import DashboardSchedulePage from "@/pages/dashboard/SchedulePage";
import PaymentsPage from "@/pages/admin/Payments";
import MealPlansPage from "@/pages/dashboard/MealPlansPage";
// FitnessTrackingPage removed — /dashboard/fitness uses AdminFitness. Re-add if a separate tracking page is built.
import SectionNotFound from "@/pages/dashboard/SectionNotFound";
import SettingsPage from "@/pages/settings/SettingsPage";
import ParentProfilePage from "@/pages/parent/profile";
import ParentSettingsPage from "@/pages/parent/SettingsPage";
import ConnectChildPage from "@/pages/parent/connect-child-page";
import FullCalendarPage from "@/pages/parent/FullCalendarPage";
import PaymentTransactionsPage from "@/pages/parent/PaymentTransactionsPage";
import EmailMailbox from "@/pages/dev/EmailMailbox";
import { DevEmailButton } from "@/components/dev/DevEmailButton";
import KidsList from "@/pages/parent/KidsList";
import KidDashboard from "@/pages/parent/KidDashboard";
import KidDashboardMVP from "@/pages/parent/KidDashboardMVP";
import ParentKidDetail from "@/pages/parent/ParentKidDetail";
import Home from "@/pages/Home";

function AppRoutes() {
  const { user, isLoading } = useAuth();

  // For testing - will display parent view when passing ?view=parent in URL
  const urlParams = new URLSearchParams(window.location.search);
  const viewParam = urlParams.get("view");
  const isTestingParentView = viewParam === "parent";

  // Show loading spinner while authentication is being verified
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
        <span className="ml-2">Loading authentication...</span>
      </div>
    );
  }

  // Check if user is a parent user
  const isParentUser = user?.role === "parent";

  return (
    <Routes>
      {/* Auth route - redirect to dashboard if authenticated */}
      <Route
        path="/auth"
        element={
          <RedirectIfAuthed>
            <AuthPageDev />
          </RedirectIfAuthed>
        }
      />

      {/* Main dashboard route with nested routes - require authentication */}
      <Route
        path="/dashboard"
        element={
          <RequireAuth>
            <DashboardLayout />
          </RequireAuth>
        }
      >
        {/* Dashboard index route */}
        <Route
          index
          element={
            isParentUser ? (
              <Navigate to="/parent" replace />
            ) : isTestingParentView ? (
              <EnhancedParentDashboard />
            ) : (
              <Dashboard />
            )
          }
        />
        
        {/* Dashboard section routes */}
        <Route path="team" element={<TeamPage />} />
        <Route path="announcements" element={<DashboardAnnouncementsPage />} />
        <Route path="schedule" element={<DashboardSchedulePage />} />
        <Route path="schedule/:id" element={<SessionDetail />} />
        <Route path="payments" element={<PaymentsPage />} />
        <Route path="meal-plans" element={<MealPlansPage />} />
        <Route path="fitness" element={<AdminFitness />} />
        <Route path="settings" element={<SettingsPage />} />
        
        {/* Admin Routes — role-gated. players/pending-review is coach-accessible
            (coaches approve registrations, task #26) so it gets its own
            admin-or-coach check, same as /coach. The other 4 are gated
            admin-only server-side (ADMIN_ROLES / ROSTER_ADMIN_ROLES, not
            isStaff) — a coach passing an admin||coach client gate on those
            would just hit 403s on every action, so they share a stricter
            admin-only wrapper instead of reusing the /coach pattern verbatim. */}
        <Route
          path="admin/players/pending-review"
          element={
            user?.role === "admin" || user?.role === "coach" ? (
              <PlayersPendingReviewPage />
            ) : (
              <Navigate to="/dashboard" replace />
            )
          }
        />
        <Route
          path="admin"
          element={
            user?.role === "admin" ? (
              <Outlet />
            ) : (
              <Navigate to="/dashboard" replace />
            )
          }
        >
          <Route path="reminders" element={<AdminReminders />} />
          <Route path="players/import" element={<PlayerImportPage />} />
          <Route path="ai-tasks" element={<AiTaskQueuePage />} />
          <Route path="agent-schedules" element={<AgentSchedulesPage />} />
        </Route>
        
        {/* (Removed orphan duplicate /dashboard/coach/schedule —
            the real coach schedule lives under the /coach route group
            with CoachDashboardLayout. fix/admin-schedule-page-in-dashboard-layout) */}
      </Route>

      {/* Root path - show homepage */}
      <Route path="/" element={<Home />} />
      
      {/* Admin redirect - move outside dashboard routes */}
      <Route path="/admin" element={<Navigate to="/dashboard" replace />} />

      {/* Parent Portal Routes - Single Layout with Outlet */}
      <Route
        path="/parent"
        element={
          <RequireAuth>
            {user?.role === "parent" ? (
              <ParentDashboardLayout />
            ) : (
              <Navigate to="/dashboard" replace />
            )}
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/parent/kids" replace />} />
        {/* /parent/dashboard is the conventional parent-landing URL used by
            external integrations and the launch certification suite. We map it
            to the same KidsList component the index redirect points at, so the
            two URLs render the same page (and the launch cert can stay agnostic
            to which one is canonical). */}
        <Route path="dashboard" element={<KidsList />} />
        <Route path="kids" element={<KidsList />} />
        <Route path="add-kid" element={<AddKid />} />
        <Route path="kids/:kidId" element={<KidDashboardMVP />} />
        <Route path="kids/:kidId/dashboard" element={<KidDashboard />} />
        <Route path="kid/:id" element={<ParentKidDetail />} />
        <Route path="schedule" element={<ParentSchedule />} />
        <Route path="schedule/full" element={<FullCalendarPage />} />
        <Route path="announcements" element={<ParentAnnouncementsPage />} />
        <Route path="payments" element={<ParentPaymentsPage />} />
        <Route path="payments/:id" element={<ParentPaymentDetailPage />} />
        <Route path="payments/transactions" element={<PaymentTransactionsPage />} />
        <Route path="connect-child" element={<ConnectChildPage />} />
        <Route path="profile" element={<ParentProfilePage />} />
        <Route path="settings" element={<ParentSettingsPage />} />
        {/* Real per-game (not practice-session) availability — distinct
            feature from /parent/schedule's practice RSVP, backed by
            GET/POST /api/games (server/routes/games.ts). The old mock page
            this route used to redirect away from is gone for good; this is
            the real build. */}
        <Route path="availability" element={<ParentAvailability />} />
      </Route>
      
      {/* Parent dashboard redirect */}
      <Route path="/dashboard/parent" element={<Navigate to="/parent" replace />} />

      {/* Coach Routes - Dedicated Coach Dashboard Layout */}
      <Route
        path="/coach"
        element={
          <RequireAuth>
            {user?.role === "admin" || user?.role === "coach" ? (
              <DashboardLayout />
            ) : (
              <Navigate to="/dashboard" replace />
            )}
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/coach/dashboard" replace />} />
        <Route path="dashboard" element={<CoachDashboard />} />
        <Route path="sessions" element={<CoachSessions />} />
        <Route path="batch-entry" element={<CoachBatchEntry />} />
        <Route path="teams" element={<TeamManagement />} />
        <Route path="teams/:id" element={<TeamDetail />} />
        <Route path="players/:id" element={<CoachKidDetail />} />
        <Route path="players/:id/performance-capture" element={<PerformanceCapturePage />} />
        <Route path="batches" element={<BatchesPage />} />
        <Route path="batches/:batchId/analytics" element={<BatchAnalyticsPage />} />
        <Route path="schedule" element={<CoachSchedule />} />
      </Route>

      {/* Hub demos — public, no auth required */}
      <Route path="/hub" element={<Navigate to="/hub/dashboard-demo" replace />} />
      <Route path="/hub/dashboard-demo" element={<HubDashboardDemo />} />
      <Route path="/hub/tournament-demo" element={<HubTournamentDemo />} />
      <Route path="/hub/selection-demo" element={<HubSelectionDemo />} />
      <Route path="/hub/performance-stats-demo" element={<HubPerformanceStatsDemo />} />
      <Route path="/hub/reports-demo" element={<HubReportsDemo />} />
      <Route path="/hub/announcements-demo" element={<HubAnnouncementsDemo />} />
      <Route path="/hub/games-schedule-demo" element={<HubGamesScheduleDemo />} />
      <Route path="/hub/teams-rosters-demo" element={<HubTeamsRostersDemo />} />
      <Route path="/hub/age-verification-demo" element={<HubAgeVerificationDemo />} />
      <Route path="/hub/fitness-tests-demo" element={<HubFitnessTestsDemo />} />
      <Route path="/hub/coming-soon/:section" element={<HubComingSoonStub />} />

      <Route path="/simple-parent" element={<SimpleReactDashboard />} />
      <Route path="/independent-parent" element={<IndependentDashboard />} />
      <Route path="/test-enhanced-parent" element={<EnhancedParentDashboard />} />

      {/* Legacy player routes - redirect to dashboard */}
      <Route path="/players" element={<Navigate to="/dashboard/team" />} />
      <Route path="/players/add" element={<Navigate to="/dashboard/team?add=true" />} />

      {/* Settings route (outside dashboard) */}
      <Route
        path="/settings"
        element={
          user ? (
            <div className="p-6">
              <h1 className="text-2xl font-bold mb-4">Settings</h1>
              <p>Settings page is coming soon!</p>
            </div>
          ) : (
            <Navigate to="/auth" />
          )
        }
      />

      {/* Common routes for all users */}
      <Route
        path="/profile"
        element={user ? <ProfilePage /> : <Navigate to="/auth" />}
      />

      {/* Public routes */}
      <Route path="/emergency-logout" element={<ForceLogoutPage />} />
      
      {/* Debug routes - accessible without login */}
      <Route path="/register-debug" element={<RegisterDebug />} />
      
      {/* Dev-only routes */}
      <Route path="/dev/emails" element={<EmailMailbox />} />

      {/* Auth routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      {/* Catch-all route */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider
          defaultTheme="light"
          storageKey="cricket-academy-theme-v2"
        >
          <BrowserRouter>
            <AppRoutes />
            <DevEmailButton />
          </BrowserRouter>
          <Toaster />
          <SonnerToaster />
          <OfflineDetector />
          <OnlineStatusIndicator />
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;